import { useState, useMemo, type CSSProperties } from 'react'
import { Search, X, ArrowLeft, Wallet } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button, Input } from '@/components/ui'
import { SectionHeader } from '@/components/hearth'
import { TransactionsList, transactionCategoryLabels } from '@/components/financial'
import { ParentSelector } from '@/components/family'
import { useFamily, useTransactions, useRoleBasedMemberId, usePermissions } from '@/hooks'
import { cn } from '@/lib/utils'
import type { TransactionCategory } from '@/types'

type FilterType = 'all' | 'flagged' | 'suspicious' | TransactionCategory

const quickFilters: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'suspicious', label: 'Worth a look' },
]

function stagger(n: number): CSSProperties {
  return { '--stagger': n } as CSSProperties
}

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'hearth-press shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors',
        selected
          ? 'bg-primary-600 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      )}
    >
      {label}
    </button>
  )
}

function EmptyState({ hasActiveFilters }: { hasActiveFilters: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-400/10 text-gray-400">
        <Search className="h-6 w-6" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-gray-900">No transactions found</h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        {hasActiveFilters
          ? 'Nothing matches right now. Try a different search or filter.'
          : 'Transactions will appear here once a bank account is connected and syncs.'}
      </p>
    </div>
  )
}

export function TransactionsPage() {
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterType>(
    searchParams.get('filter') === 'flagged' ? 'flagged' : 'all'
  )
  const { selectedFamilyId } = useFamily()
  const { shouldShowMemberSelector } = useRoleBasedMemberId()
  const { canViewFinancial } = usePermissions()

  // Build API params based on filters
  const apiParams = useMemo(() => {
    const params: {
      page: number
      page_size: number
      search?: string
      category?: TransactionCategory
      flagged?: boolean
    } = {
      page,
      page_size: 20,
    }

    if (search) {
      params.search = search
    }

    if (activeFilter === 'flagged') {
      params.flagged = true
    } else if (activeFilter !== 'all' && activeFilter !== 'suspicious') {
      params.category = activeFilter
    }

    return params
  }, [page, search, activeFilter])

  const {
    transactions,
    pagination,
    isLoading,
    flagTransaction,
    unflagTransaction,
  } = useTransactions(selectedFamilyId, apiParams)

  // Filter suspicious transactions client-side (anomaly_score > 0.7)
  const displayTransactions = useMemo(() => {
    if (activeFilter === 'suspicious') {
      return transactions.filter((t) => t.anomaly_score > 0.7)
    }
    return transactions
  }, [transactions, activeFilter])

  // Adjust pagination for client-side filtering
  const displayPagination = useMemo(() => {
    if (activeFilter === 'suspicious' && pagination) {
      return {
        ...pagination,
        total_count: displayTransactions.length,
        total_pages: 1,
      }
    }
    return pagination
  }, [activeFilter, pagination, displayTransactions.length])

  const handleFlag = async (transactionId: string) => {
    await flagTransaction({
      transactionId,
      data: { reason: 'SUSPICIOUS' },
    })
  }

  const handleUnflag = async (transactionId: string) => {
    await unflagTransaction(transactionId)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const selectFilter = (filter: FilterType) => {
    setActiveFilter(filter)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setActiveFilter('all')
    setPage(1)
  }

  const hasActiveFilters = Boolean(search) || activeFilter !== 'all'

  // Permission gate
  if (!canViewFinancial) {
    return (
      <div className="hearth-appear flex flex-col items-center justify-center py-16 text-center" style={stagger(0)}>
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
          <Wallet className="h-8 w-8 text-primary-600" />
        </span>
        <h3 className="mt-4 font-serif text-xl font-semibold text-gray-900">
          You don't have access yet
        </h3>
        <p className="mt-2 max-w-sm text-sm text-gray-500">
          Ask a family admin to grant you permission to view financial information.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="hearth-appear flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        style={stagger(0)}
      >
        <div className="flex items-center gap-4">
          <Link to="/financial">
            <Button variant="ghost" size="sm" className="gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="font-serif text-2xl font-semibold text-gray-900">Transactions</h1>
            <p className="text-gray-500">Every transaction from connected accounts.</p>
          </div>
        </div>
        {shouldShowMemberSelector && <ParentSelector />}
      </div>

      {/* Search + filter chips */}
      <div className="hearth-appear space-y-3" style={stagger(1)}>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search transactions…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {quickFilters.map(({ value, label }) => (
            <FilterChip
              key={value}
              label={label}
              selected={activeFilter === value}
              onClick={() => selectFilter(value)}
            />
          ))}
          <span className="h-5 w-px shrink-0 bg-gray-200" />
          {(Object.keys(transactionCategoryLabels) as TransactionCategory[]).map((category) => (
            <FilterChip
              key={category}
              label={transactionCategoryLabels[category]}
              selected={activeFilter === category}
              onClick={() => selectFilter(category)}
            />
          ))}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex shrink-0 items-center gap-1 px-2 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Transactions list */}
      <section className="hearth-appear space-y-3" style={stagger(2)}>
        <SectionHeader
          title={
            displayPagination
              ? `${displayPagination.total_count} transaction${displayPagination.total_count === 1 ? '' : 's'}`
              : 'Transactions'
          }
        />
        <div className="hearth-card overflow-hidden">
          {!isLoading && displayTransactions.length === 0 ? (
            <EmptyState hasActiveFilters={hasActiveFilters} />
          ) : (
            <TransactionsList
              transactions={displayTransactions}
              isLoading={isLoading}
              onFlag={handleFlag}
              onUnflag={handleUnflag}
              pagination={displayPagination}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </section>
    </div>
  )
}
